import Chip, { ChipProps } from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";
import { CSSTextAlign } from "core/types";
import Tooltip from "ds_legacy/components/Tooltip";
import { dp, sizing } from "ds_legacy/materials/metrics";
import {
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_REGULAR,
} from "ds_legacy/materials/typography";
import { useMemo } from "react";

export type InfoChipProps = Pick<
  ChipProps,
  "color" | "disabled" | "icon" | "label" | "size" | "sx" | "variant"
> & {
  as?: React.ElementType;
  isBold?: boolean;
  margin?: string;
  testId?: string;
  tooltip?: string;
  tooltipTextAlign?: CSSTextAlign;
};

export default function InfoChip({
  as,
  color = "default",
  disabled = false,
  icon,
  isBold,
  label,
  margin,
  size = "small",
  sx,
  testId,
  tooltip,
  tooltipTextAlign = "center",
  variant = "filled",
}: InfoChipProps) {
  const theme = useTheme();
  const paletteKey = color === "default" ? "info" : color;

  const sxConfig = useMemo(
    () => ({
      "&.MuiChip-colorSecondary": {
        backgroundColor:
          variant === "outlined"
            ? theme.palette.common.white
            : theme.palette[paletteKey].main,
        borderColor:
          variant === "outlined"
            ? theme.palette[paletteKey].main
            : theme.palette.common.white,
        color: theme.palette.common.black,
      },
      "&.MuiChip-colorSuccess": {
        backgroundColor:
          variant === "outlined"
            ? theme.palette.common.white
            : theme.palette[paletteKey].main,
        borderColor:
          variant === "outlined"
            ? theme.palette[paletteKey].main
            : theme.palette.common.white,
        color:
          variant === "outlined"
            ? theme.palette[paletteKey].main
            : theme.palette.common.white,
      },
      "& .MuiChip-icon": {
        marginLeft: sizing(1),
      },
      "& .MuiChip-label": {
        fontWeight: isBold ? FONT_WEIGHT_BOLD : FONT_WEIGHT_REGULAR,
        whiteSpace: "normal",
      },
      borderColor:
        variant === "outlined" && color === "default"
          ? theme.palette.grey[600]
          : theme.palette[paletteKey].main,
      ...sx,
    }),
    [isBold, color, variant, theme, ...Object.values(sx ?? {})],
  );

  const Container = as ?? "div";

  return (
    <Container data-testid={testId} style={{ margin: margin }}>
      <Tooltip title={tooltip} tooltipTextAlign={tooltipTextAlign}>
        <Chip
          color={color}
          disabled={disabled}
          icon={icon}
          label={label}
          size={size}
          sx={{
            ...sxConfig,
            height: "auto",
            minHeight: dp(24),
            wordBreak: "break-word",
          }}
          variant={variant}
        />
      </Tooltip>
    </Container>
  );
}
